import { getConfig } from '@/../config/environment';
import { ApiLinkModel, LinkTypeModel, IApiContentRouteModel } from '@/services/platform/api/model';

export interface Link {
  href: string;
  label: string;
  target?: string;
  newTab?: boolean;
  linkType?: LinkTypeModel;
  route?: IApiContentRouteModel | null | undefined;
}

/**
 * Maps an array of ApiLinkModels to a single Link (first item).
 */
export const LinksToLinkMap = (source?: ApiLinkModel[]): Link | undefined => {
  if (!source || source.length === 0) return undefined;
  return LinkMap(source[0]);
};

/**
 * Maps an array of ApiLinkModels to an array of Links.
 */
export const LinksMap = (source?: ApiLinkModel[]): Link[] => {
  if (!source) return [];
  return source.map(LinkMap).filter((l): l is Link => !!l);
};

/**
 * Maps a single ApiLinkModel to a simplified frontend Link.
 */
export const LinkMap = (source?: ApiLinkModel): Link | undefined => {
  if (!source) return undefined;
  const config = getConfig();

  let href = '';

  switch (source.linkType) {
    case 'Content':
      // Internal Umbraco route
      href = source.route?.path ?? '';
      if (source.route?.queryString) {
        href +=
          source.route.queryString.startsWith('?') || source.route.queryString.startsWith('#')
            ? source.route.queryString
            : `?${source.route.queryString}`;
      } else if (source?.queryString) {
        href +=
          source.queryString.startsWith('?') || source.queryString.startsWith('#')
            ? source.queryString
            : `?${source.queryString}`;
      }
      break;

    case 'Media':
      // Media files (e.g., PDFs)
      href = `${config.mediaUrl ?? ''}${source.url ?? ''}`;
      break;

    case 'External':
    default:
      // External or manual URL
      href = source.url ?? '';
      break;
  }

  if (!href) return undefined;

  return {
    href,
    label: source.title ?? '',
    target: source.target ?? undefined,
    newTab: source.target === '_blank',
    linkType: source.linkType,
    route: source.route,
  };
};

export const LinkRouteUrl = (route?: IApiContentRouteModel): string | undefined => {
  if (!route) return undefined;
  if (route.queryString) {
    return `${route.path}${
      route.queryString.startsWith('?') || route.queryString.startsWith('#')
        ? route.queryString
        : `?${route.queryString}`
    }`;
  }
  return route.path;
};

/**
 * Remove the last segment from a URL or path string.
 * Example: '/uk/careers/demand-planning-manager-only-uk' => '/uk/careers'
 */
export function removeLastSegment(path: string): string {
  const segments = path.split('/').filter(Boolean); // remove empty segments
  segments.pop(); // remove last segment
  return '/' + segments.join('/');
}
